import { updateState } from '@angular-architects/ngrx-toolkit';
import { tapResponse } from '@ngrx/operators';
import { signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';

import { computed, inject } from '@angular/core';
import { combineLatest, Observable, pipe, switchMap } from 'rxjs';

import { MijnLaunchDarklyService } from '@ap/mijn-kanaalconfiguratie';

import { withDebugDevTools } from '../../../debug/devtools.features';
import {
    ALL_KILL_SWITCHES,
    CONFIG_SEAMLY,
    DEFAULT_CONFIG_SEAMLY,
    KillSwitchResponse
} from '../../klant/conversational-connector/klant-conversational-connector.constants';
import {
    PlatformOperationalSettings,
    SeamlyApiDomain,
    SeamlyConfiguration,
    SeamlyEnvironment
} from '../../klant/conversational-connector/klant-conversational-connector.model';
import { ConversationalChatStore } from '../conversational-chat-store/conversational-chat.store';

type ConversationalConfigState = {
    isLoading: {
        seamlyConfig: boolean;
        killSwitches: boolean;
    };
    errorGettingConfiguration: boolean;
    errorGettingKillSwitches: boolean;
    seamlyConfiguration: SeamlyConfiguration;
    isPlatformOperational: PlatformOperationalSettings;
    deadmanSwitchEnabled: boolean;
};

export const initialState: ConversationalConfigState = {
    isLoading: {
        seamlyConfig: true,
        killSwitches: true
    },
    errorGettingConfiguration: false,
    errorGettingKillSwitches: false,
    deadmanSwitchEnabled: false,
    isPlatformOperational: {
        mijn: true,
        mobiel: true,
        retail: true
    },
    seamlyConfiguration: {
        environment: '' as unknown as SeamlyEnvironment,
        seamlyApiKey: '',
        seamlyApiDomain: '' as unknown as SeamlyApiDomain,
        seamlyLocalVersion: 0,
        locale: ''
    }
};

export type ConversationalConfigStore = InstanceType<typeof ConversationalConfigStore>;

export const ConversationalConfigStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withDebugDevTools('conversational-config'),
    withComputed((store) => ({
        basePath: computed(() => {
            const { seamlyApiDomain, environment } = store.seamlyConfiguration();
            const prefix = environment !== 'DEV' ? 'https://' : '/services/';
            return seamlyApiDomain ? `${prefix}${seamlyApiDomain}` : undefined;
        }),
        doneLoading: computed(() => {
            const { killSwitches, seamlyConfig } = store.isLoading();
            return killSwitches === false && seamlyConfig === false;
        })
    })),
    withComputed((store, conversationalChatStore = inject(ConversationalChatStore)) => ({
        isOperational: computed(() => {
            const doneLoading = store.doneLoading();
            const platform = conversationalChatStore.platform();

            if (!doneLoading || !platform || store.deadmanSwitchEnabled() === true) {
                return false;
            }

            return store.isPlatformOperational()[platform] === true;
        }),
        shouldTriggerErrorOnMijnContactPage: computed(
            () => store.doneLoading() === true && (store.deadmanSwitchEnabled() === true || store.isPlatformOperational()?.mijn === false)
        )
    })),
    withMethods((store, mijnLaunchDarklyService = inject(MijnLaunchDarklyService)) => ({
        watchSeamlyConfiguration: rxMethod<void>(
            pipe(
                switchMap(() => mijnLaunchDarklyService.getFlagValue(CONFIG_SEAMLY, DEFAULT_CONFIG_SEAMLY, true)),
                tapResponse({
                    next: (seamlyConfiguration: SeamlyConfiguration) => {
                        updateState(store, 'Seamly configuration updated succesfully', {
                            seamlyConfiguration,
                            errorGettingConfiguration: false,
                            isLoading: {
                                ...store.isLoading(),
                                seamlyConfig: false
                            }
                        });
                    },
                    error: () => {
                        updateState(store, 'Error fetching Seamly configuration', {
                            errorGettingConfiguration: true,
                            isLoading: {
                                ...store.isLoading(),
                                seamlyConfig: false
                            }
                        });
                    }
                })
            )
        ),
        watchKillSwitches: rxMethod<void>(
            pipe(
                switchMap(() => {
                    const observables = ALL_KILL_SWITCHES.map((killSwitch) =>
                        mijnLaunchDarklyService.getFlagValue(killSwitch, false, true)
                    );
                    return combineLatest(observables) as Observable<KillSwitchResponse>;
                }),
                tapResponse({
                    next: (killSwitches: KillSwitchResponse) => {
                        updateState(store, 'Kill switches updated succesfully', {
                            deadmanSwitchEnabled: killSwitches[0],
                            isPlatformOperational: {
                                mijn: !killSwitches[1],
                                mobiel: !killSwitches[2],
                                retail: !killSwitches[3]
                            },
                            isLoading: {
                                ...store.isLoading(),
                                killSwitches: false
                            }
                        });
                    },
                    error: () => {
                        const deadmanSwitchEnabled = false;
                        const isPlatformOperational: PlatformOperationalSettings = {
                            mijn: false,
                            mobiel: false,
                            retail: false
                        };
                        updateState(store, 'Error updating kill switches', {
                            deadmanSwitchEnabled,
                            isPlatformOperational,
                            errorGettingKillSwitches: true,
                            isLoading: {
                                ...store.isLoading(),
                                killSwitches: false
                            }
                        });
                    }
                })
            )
        )
    })),
    withHooks({
        onInit(store): void {
            store.watchKillSwitches();
            store.watchSeamlyConfiguration();
        }
    })
);
